import React from 'react';
import type {
  InventoryItemRecord,
  InventoryStockLocationRecord,
  InventoryStockMovementRecord,
  InventoryUnitRecord,
} from '@/renderer/types/inventory';
import {
  formatReportDateTime,
  formatReportMoney,
} from '@/renderer/util/reporting';
import { RefreshCw } from 'lucide-react';
import { getMovementBadgeLabel, getMovementTone } from './movementPresentation';

const formatMovementReferenceType = (value: string | null) => {
  if (!value) return 'No reference type';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatMovementReferenceValue = (
  movement: InventoryStockMovementRecord,
) => {
  if (movement.reference_type === 'order') {
    return movement.order_bill_number
      ? `Bill #${movement.order_bill_number}`
      : 'Bill number unavailable';
  }

  return movement.reference_id || 'No reference ID';
};

export function StockMovementsTable({
  movements,
  items,
  locations,
  units,
  isLoading = false,
}: {
  movements: InventoryStockMovementRecord[];
  items: InventoryItemRecord[];
  locations: InventoryStockLocationRecord[];
  units: InventoryUnitRecord[];
  isLoading?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="relative max-h-[34rem] overflow-auto">
        {isLoading ? (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center bg-white/65 pt-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm">
              <RefreshCw size={13} className="animate-spin" />
              Updating movements
            </span>
          </div>
        ) : null}
        <table
          className={`w-full text-sm transition-opacity ${isLoading ? 'opacity-60' : ''}`}
          aria-busy={isLoading}
        >
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Date
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Item
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Location
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Movement
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Quantity
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Cost
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Reference
              </th>
            </tr>
          </thead>
          <tbody>
            {movements.map((movement) => {
              const item = items.find(
                (candidate) => candidate.id === movement.inventory_item_id,
              );
              const location = locations.find(
                (candidate) => candidate.id === movement.stock_location_id,
              );
              const unit = item
                ? units.find((u) => u.id === item.stock_unit_id)
                : undefined;
              const unitLabel = unit?.abbreviation || unit?.name || '';
              const tone = getMovementTone(movement.movement_type);

              return (
                <tr key={movement.id} className="border-t border-gray-100">
                  <td className="px-5 py-4 text-gray-600">
                    {formatReportDateTime(movement.movement_date)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="font-medium text-gray-900">
                      {item?.name || 'Unknown item'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {item?.sku || 'No SKU'}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {location?.name || 'Unknown location'}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.14em] ${tone.badgeClassName}`}
                    >
                      {getMovementBadgeLabel(movement.movement_type)}
                    </span>
                  </td>
                  <td
                    className={`px-5 py-4 font-medium ${tone.quantityClassName}`}
                  >
                    {movement.quantity.toLocaleString(undefined, {
                      maximumFractionDigits: 4,
                    })}
                    {unitLabel && (
                      <span className="ml-1 text-xs font-normal text-gray-400">
                        {unitLabel}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {movement.total_cost === null
                      ? '—'
                      : formatReportMoney(movement.total_cost)}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    <div>
                      {formatMovementReferenceType(movement.reference_type)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatMovementReferenceValue(movement)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
